import { Dialog, DialogWindow, useDialogManager } from '../../widgets/dialog';
import { PrimaryButton, TextButton } from '../../widgets/buttons';
import { removeNotification, showInfoNotification, showNotification } from '../../widgets/notificationToaster';
import { TableContentLoader } from '../../widgets/table';
import { useInfiniteScroll } from '../../widgets/hooks/useInfiniteScroll';
import { debounce } from 'lodash';
import React, { FC, Suspense, useCallback, useEffect, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, usePaginationFragment } from 'react-relay';
import Loader from 'src/components/widgets/loader';
import { Center } from 'src/shared/globals';
import { createEmptyPalette, T, validateAndFilterCurrentPaletteV2 } from 'src/shared/helpers';
import { usePaletteColors } from 'src/shared/hooks/usePaletteColors';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { Relay } from '../../../relay';
import { PaletteColorInput } from '../settings/colorPaletteSettings/__generated__/paletteColorsPageUpdateMutation.graphql';
import PaletteColorsContent from '../settings/colorPaletteSettings/paletteColorsContent';
import { createOrEditPaletteDialogCreateMutation } from './__generated__/createOrEditPaletteDialogCreateMutation.graphql';
import { createOrEditPaletteDialogUpdateMutation } from './__generated__/createOrEditPaletteDialogUpdateMutation.graphql';

import { createOrEditPaletteDialog_colors$key } from './__generated__/createOrEditPaletteDialog_colors.graphql';
import { createOrEditPaletteDialog_colorsQuery } from './__generated__/createOrEditPaletteDialog_colorsQuery.graphql';
import { createOrEditPaletteDialog_paletteQuery } from './__generated__/createOrEditPaletteDialog_paletteQuery.graphql';
import PantoneLibraryDialog from './pantoneLibraryDialog';

interface CreateOrEditPaletteDialogProps {
    onClosed: (createdPaletteId?: string) => void;
    editPaletteId?: string;
    paletteTypeID?: number;
    mode?: 'create' | 'edit';
    shouldShowRadioButtons?: boolean;
}

interface CreateOrEditPaletteDialogContentProps extends CreateOrEditPaletteDialogProps {
    onPaletteImported: (importedPaletteId: string) => void;
}

const FooterContainer = styled.div<{ $isCmyk?: boolean }>`
    width: 100%;
    display: flex;
    margin-top: 20px;
    justify-content: ${(props) => (props.$isCmyk ? 'flex-end' : 'space-between')};
`;

const CustomDialog = styled(DialogWindow)`
    flex: 1;
    max-width: 1300px;
    height: 100%;
    overflow-y: auto;
`;

const paletteQuery = graphql`
    query createOrEditPaletteDialog_paletteQuery($paletteId: ID!) {
        palette(id: $paletteId) {
            id
            name
            useConversionTable
            paletteTypeID
            isZakekeDefault
        }
    }
`;

const colorsQuery = graphql`
    fragment createOrEditPaletteDialog_colors on Query
    @refetchable(queryName: "createOrEditPaletteDialogColorsQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 70 }
        after: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: null }
        id: { type: "String", defaultValue: null }
        orderBy: { type: "String", defaultValue: "DESC" }
        useConversionTable: { type: "Boolean", defaultValue: true }
    ) {
        paletteColors(first: $first, after: $after, search: $search, id: $id, orderBy: $orderBy, useConversionTable: $useConversionTable)
            @connection(key: "createOrEditPaletteDialog_paletteColors") {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    colorID
                    name
                    rgbHexCode
                    cmykHexCode
                    spotColorName
                }
            }
        }
    }
`;

const CreateOrEditPaletteDialogContent: FC<CreateOrEditPaletteDialogContentProps> = ({
    onClosed,
    onPaletteImported,
    editPaletteId,
    paletteTypeID,
    mode = 'create',
    shouldShowRadioButtons = false
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const validationContext = useValidationContext();
    const [isSaving, setIsSaving] = useState(false);
    const [isPending, startTransition] = useTransition();
    const [search, setSearch] = useState('');
    const [searchFilter, setSearchFilter] = useState('');

    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>();

    // Fetch palette data if editing
    const paletteData = editPaletteId
        ? useLazyLoadQuery<createOrEditPaletteDialog_paletteQuery>(paletteQuery, { paletteId: editPaletteId }, { fetchPolicy: 'network-only' })
        : null;

    // Fetch palette colors with pagination
    const paletteColorsQueryData = editPaletteId
        ? useLazyLoadQuery<createOrEditPaletteDialog_colorsQuery>(
              graphql`
                  query createOrEditPaletteDialog_colorsQuery(
                      $first: Int
                      $after: String
                      $search: String
                      $id: String
                      $orderBy: String
                      $useConversionTable: Boolean
                  ) {
                      ...createOrEditPaletteDialog_colors
                          @arguments(first: $first, after: $after, search: $search, id: $id, orderBy: $orderBy, useConversionTable: $useConversionTable)
                  }
              `,
              { first: 70, after: '1', search: searchFilter || null, id: editPaletteId, orderBy: 'DESC', useConversionTable: true },
              { fetchPolicy: 'network-only' }
          )
        : null;

    const { data, loadNext, refetch, hasNext, isLoadingNext } = paletteColorsQueryData
        ? usePaginationFragment<createOrEditPaletteDialog_colorsQuery, createOrEditPaletteDialog_colors$key>(colorsQuery, paletteColorsQueryData)
        : { data: null, loadNext: () => {}, refetch: () => {}, hasNext: false, isLoadingNext: false };

    // Infinite scroll implementation
    useInfiniteScroll(
        () =>
            startTransition(() => {
                loadNext(70);
            }),
        hasNext,
        scrollContainer, // Pass the scroll container element
        isLoadingNext
    );

    // Use the centralized hook for managing palette colors
    const {
        currentColors,
        addedColors,
        editedColors,
        deletedColors,
        setEditedColors,
        setCurrentColors,
        addColor,
        addColors,
        deleteColor,
        updateColor,
        resetColor,
        removeEmptyColors,
        handleCurrentColorsChange,
        currentPalette,
        handlePaletteNameChange,
        handlePaletteTypeChange,
        findColorById,
        isTemporaryColor,
        filterSearchedColors
    } = usePaletteColors(
        paletteData?.palette ? { ...paletteData.palette, colors: [] } : createEmptyPalette(paletteTypeID),
        data?.paletteColors?.edges?.map((edge) => edge.node) || [],
        !!searchFilter
    );

    const setSearchDebounced = useCallback(
        debounce((filter: string) => {
            setSearchFilter(filter);
        }, 300),
        []
    );

    const handleSearchColor = useCallback(
        (search: string) => {
            setSearch(search);
            setSearchDebounced(search);
        },
        [setSearchDebounced]
    );

    const handlePantoneIconClick = (index: number, color: PaletteColorInput) => {
        // All colors should have IDs (either real IDs from database or temporary IDs for added colors)
        if (!color.id) return;

        // Store the color ID that we're replacing
        const colorIdToReplace = color.id;

        // If it's not a temporary color and not already in editedColors, add it
        if (!isTemporaryColor(colorIdToReplace) && editedColors.every((editedColor) => editedColor.id !== colorIdToReplace)) {
            setEditedColors((prev) => [color, ...prev]);
        }

        setSearch('');
        setSearchFilter('');

        showDialog(
            'pantone-library-dialog-single-selection',
            <PantoneLibraryDialog
                onClosed={(selectedColors?: PaletteColorInput[]) => {
                    closeDialog('pantone-library-dialog-single-selection');
                    if (selectedColors) {
                        handleAddSelectedColors(selectedColors, colorIdToReplace);
                    }
                }}
                onPaletteImported={(importedPaletteId: string) => {
                    closeDialog('pantone-library-dialog-single-selection');
                    onPaletteImported?.(importedPaletteId);
                }}
                singleSelection={true}
            />
        );
    };

    const handleReplaceColor = (selectedColors: PaletteColorInput[], replacingColorID: string) => {
        const newColor = selectedColors[0]; // Only take the first selected color
        const { color: colorToReplace, index: colorIndex } = findColorById(replacingColorID);

        if (colorToReplace && colorIndex !== -1) {
            // Create the updated color with the original ID to maintain consistency
            const updatedColor = { ...newColor, id: replacingColorID };

            if (isTemporaryColor(replacingColorID)) {
                // If the color being replaced is a temporary added color, treat it as an addition
                updateColor(colorIndex, updatedColor);
            } else {
                // If the color being replaced is an existing color, treat it as an edit
                const editIndex = editedColors.findIndex((color) => color.id === replacingColorID);
                updateColor(editIndex !== -1 ? editIndex : colorIndex, updatedColor, editIndex !== -1);
            }
        }
    };

    // Update colors when palette data is loaded (only for edit mode)
    useEffect(() => {
        if (editPaletteId && data?.paletteColors?.edges) {
            const filteredColors = filterSearchedColors(
                data.paletteColors.edges.map((edge) => edge.node),
                searchFilter
            );
            setCurrentColors(filteredColors);
        }
    }, [data]);

    const [createPalette, isCreatingPalette] = useMutation<createOrEditPaletteDialogCreateMutation>(graphql`
        mutation createOrEditPaletteDialogCreateMutation($input: PaletteInput!) {
            createPalette(input: $input) {
                palette {
                    ...paletteFragment_palette @relay(mask: false)
                }
            }
        }
    `);

    const [updatePalette, isUpdatingPalette] = useMutation<createOrEditPaletteDialogUpdateMutation>(graphql`
        mutation createOrEditPaletteDialogUpdateMutation($input: PaletteInput!) {
            updatePalette(input: $input) {
                palette {
                    ...paletteFragment_palette @relay(mask: false)
                }
            }
        }
    `);

    const handleSavePalette = async () => {
        if (!validationContext.submit()) return;

        const { isPaletteValid, errorMessage, input } = validateAndFilterCurrentPaletteV2(currentPalette, mode === 'edit');

        if (!isPaletteValid) {
            showNotification({
                type: 'danger',
                message: T._(errorMessage || 'Palette is invalid.', 'Admin'),
                dismiss: { duration: 9000 }
            });
            return;
        }

        const savePaletteNotification = showInfoNotification({
            message: T._(
                mode === 'create' ? `Creating Palette... please don't close the dialog.` : `Updating Palette... please don't close the dialog.`,
                'Admin'
            ),
            dismiss: undefined
        });

        setIsSaving(true);

        try {
            if (mode === 'create') {
                await createPalette({
                    variables: { input },
                    onCompleted: (res) => {
                        removeNotification(savePaletteNotification);
                        showNotification({
                            type: 'success',
                            message: T._('Palette successfully created. You can find it in "Settings > Pantone/CMYK Palettes".', 'Admin'),
                            dismiss: { duration: 5000 }
                        });
                        onClosed(res.createPalette?.palette?.id);
                    },
                    onError: (error) => {
                        console.error('Error creating palette:', error);
                        removeNotification(savePaletteNotification);
                        showNotification({
                            type: 'danger',
                            message: T._('Error saving palette. Please try again.', 'Admin'),
                            dismiss: { duration: 9000 }
                        });
                        setIsSaving(false);
                    }
                });
            } else {
                await updatePalette({
                    variables: { input },
                    onCompleted: (res) => {
                        removeNotification(savePaletteNotification);
                        showNotification({
                            type: 'success',
                            message: T._('Palette saved successfully.', 'Admin'),
                            dismiss: { duration: 5000 }
                        });
                        onClosed(res.updatePalette?.palette?.id);
                    },
                    onError: (error) => {
                        console.error('Error updating palette:', error);
                        removeNotification(savePaletteNotification);
                        showNotification({
                            type: 'danger',
                            message: T._('Error saving palette. Please try again.', 'Admin'),
                            dismiss: { duration: 9000 }
                        });
                        setIsSaving(false);
                    }
                });
            }
        } catch (error) {
            console.error('Error in handleSavePalette:', error);
            removeNotification(savePaletteNotification);
            showNotification({
                type: 'danger',
                message: T._('Error saving palette. Please try again.', 'Admin'),
                dismiss: { duration: 9000 }
            });
            setIsSaving(false);
        }
    };

    const handleAddSelectedColors = (colors: PaletteColorInput[], replacingColorID?: string) => {
        if (replacingColorID) {
            // If we're replacing a color, use the replace logic
            handleReplaceColor(colors, replacingColorID);
        } else {
            // If we're adding colors normally
            addColors(colors);
            removeEmptyColors();
        }
    };

    const handleChooseFromLibrary = () => {
        setSearch('');
        setSearchFilter('');

        showDialog(
            'pantone-library-dialog',
            <PantoneLibraryDialog
                onClosed={(selectedColors?: PaletteColorInput[]) => {
                    closeDialog('pantone-library-dialog');
                    if (selectedColors) {
                        handleAddSelectedColors(selectedColors);
                    }
                }}
                onPaletteImported={(importedPaletteId: string) => {
                    closeDialog('pantone-library-dialog');
                    onPaletteImported?.(importedPaletteId);
                }}
                singleSelection={false}
            />
        );
    };

    return (
        <Dialog
            windowDecorator={CustomDialog}
            footerContent={
                <>
                    {(!paletteData || (paletteData && paletteData.palette?.isZakekeDefault === false)) && (
                        <FooterContainer $isCmyk={currentPalette.paletteTypeID === 2}>
                            {currentPalette.paletteTypeID === 1 && (
                                <TextButton onClick={handleChooseFromLibrary}>{T._('Start with pre-configured colors', 'Admin')}</TextButton>
                            )}
                            <PrimaryButton onClick={handleSavePalette} disabled={isUpdatingPalette || isCreatingPalette}>
                                {mode === 'edit' ? T._('Save Palette', 'Admin') : T._('Create Palette', 'Admin')}
                            </PrimaryButton>
                        </FooterContainer>
                    )}
                </>
            }
            ref={(instance) => setScrollContainer(instance)}
        >
            <PaletteColorsContent
                currentPalette={currentPalette}
                currentColors={currentColors}
                onDelete={deleteColor}
                onAdd={addColor}
                onEdit={updateColor}
                onReset={resetColor}
                onPantoneIconClick={handlePantoneIconClick}
                onPaletteNameChange={handlePaletteNameChange}
                isZakekeDefault={paletteData?.palette?.isZakekeDefault}
                onHandleChooseFromLibraryClick={handleChooseFromLibrary}
                onSearchColor={handleSearchColor}
                searchValue={search}
                hasColors={!!editPaletteId}
                onChangeColorType={handlePaletteTypeChange}
                shouldShowRadioButtons={shouldShowRadioButtons}
                paletteNameValidationKey='printTypeDialogPaletteName'
                paletteColorsValidationKey='printTypeDialogPaletteColors'
                validatePaletteColors={(value) => !validateAndFilterCurrentPaletteV2(value, mode === 'edit').isPaletteValid}
                paletteColorsValidationMessage={T._('Every color must have the required code, a name, and a valid RGB hex code.', 'Admin')}
            />

            {isPending ||
                (hasNext && isLoadingNext && (
                    <Center style={{ marginTop: '15px' }}>
                        <Loader />
                    </Center>
                ))}

            {isPending && <TableContentLoader />}
        </Dialog>
    );
};

const CreateOrEditPaletteDialog: FC<CreateOrEditPaletteDialogProps> = ({ editPaletteId, mode = 'create', ...rest }) => {
    const [currentPaletteId, setCurrentPaletteId] = useState(editPaletteId);
    const [currentMode, setCurrentMode] = useState(mode);
    const [, startTransition] = useTransition();

    const handlePaletteImported = (importedPaletteId: string) => {
        startTransition(() => {
            setCurrentPaletteId(importedPaletteId);
            setCurrentMode('edit');
        });
        showNotification({
            type: 'success',
            message: T._('Palette imported successfully.', 'Admin'),
            dismiss: { duration: 3000 }
        });
    };

    return (
        <Relay>
            <Suspense fallback={<Loader />}>
                <ValidationStateProvider>
                    <CreateOrEditPaletteDialogContent
                        {...rest}
                        key={currentPaletteId || 'new'}
                        editPaletteId={currentPaletteId}
                        mode={currentMode}
                        onPaletteImported={handlePaletteImported}
                    />
                </ValidationStateProvider>
            </Suspense>
        </Relay>
    );
};

export default CreateOrEditPaletteDialog;
